import { InfoCircle } from '@untitledui/icons';
import { useId, useMemo } from 'react';

import type { NativeReportResponse } from '@/api/reports';
import { EmptyState } from '@/lib/base/empty-state';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { QueryErrorState } from '@/lib/components';
import { logger } from '@/lib/logger';
import type { ChartDatapointSelection, ChartType } from '@/types/reports';

import {
  formatMeasureValue,
  formatXLabel,
  isDateDimensionWithGranularity,
  NO_DATE_LABEL,
} from '../constants';
import type { ReportTableInteraction } from '../ReportDataTable';
import { ReportDataTable } from '../ReportDataTable';
import type {
  BarChartOptions,
  ChartConfiguration,
  LineChartOptions,
} from '../types';
import { DEFAULT_CHART_CONFIG } from '../types';
import { applyTopN } from '../utils/applyTopN';
import { computeValueAxisMeta } from '../utils/chartAxisMeta';
import { currencyBreakdownTooltip } from '../utils/currencyAggregate';
import { groupMeasuresByUnit } from '../utils/measureUnits';
import { resolveMultiMeasureLayout } from '../utils/multiMeasureLayout';
import {
  isAggregationColumn,
  nativeToChartData,
  nativeToTableColumns,
  nativeToTablePivot,
  NON_MEASURE_ROW_KEYS,
  sortChartDataChronologically,
  sortTableDataChronologically,
} from '../utils/nativeDataTransforms';
import { AreaChartCase } from './AreaChartCase';
import { BarChartCase } from './BarChartCase';
import {
  EARLIEST_RATE_NOTE_HEIGHT,
  hasEarliestRateConversion,
} from './earliestRateConversion';
import { EarliestRateNote } from './EarliestRateNote';
import { FunnelChartCase } from './FunnelChartCase';
import { LineChartCase } from './LineChartCase';
import { MetricChartCase } from './MetricChartCase';
import { MultiMeasureCartesianChart } from './MultiMeasureCartesianChart';
import { PieChartCase } from './PieChartCase';
import {
  DEFAULT_AREA_OPTIONS,
  DEFAULT_BAR_OPTIONS,
  DEFAULT_LINE_OPTIONS,
  FALLBACK_SERIES_COLOR,
  resolveCssColor,
} from './shared';

export interface ChartRendererProps {
  query?: unknown;
  chartType: ChartType;
  config?: ChartConfiguration;
  isLoading?: boolean;
  error?: Error | null;
  /**
   * Function to resolve human-readable labels for dimension values.
   * If not provided, raw values are displayed.
   */
  resolveLabel?: (value: string, dimensionName?: string) => string;
  /**
   * The primary dimension name (e.g., 'CrmObject.ahoy_lifecycle_stage').
   * Used to provide context to resolveLabel for smarter label inference.
   */
  dimensionName?: string;
  /**
   * All dimension names in order (e.g., ['deals.pipeline', 'deals.stage']).
   * When provided, multi-dimension chart labels resolve each value separately
   * using the corresponding dimension name for context.
   */
  dimensionNames?: string[];
  /**
   * ISO 4217 currency code for formatting monetary values (e.g., 'USD', 'EUR', 'GBP').
   * Defaults to 'USD' if not provided.
   */
  currency?: string;
  /**
   * Native report response data (alternative to resultSet for native reporting backend)
   */
  nativeData?: NativeReportResponse;
  /**
   * Native chart type identifier (when using nativeData)
   */
  nativeChartType?: string;
  /**
   * Chart area height in pixels. Defaults to 400 — the compact size used by
   * dashboard cards. Larger surfaces (the builder's live preview) pass a
   * viewport-proportional value so the chart uses the available space.
   */
  height?: number;
  /**
   * Tile-scale rendering. Only the `metric` case reads it today — a KPI is one
   * glyph whose size has to answer to the card it sits in, where every other
   * chart type scales through `height`.
   */
  compact?: boolean;
  /**
   * Called when the user clicks (or keyboard-activates) a chart datapoint.
   * When omitted the chart is purely read-only — no cursor or dim changes.
   */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint, used to dim all others. */
  activeSelection?: ChartDatapointSelection | null;
  /**
   * Org whose records the table view's entity cells resolve against.
   *
   * Supplied alongside `onDatapointSelect` it makes the table's cells
   * actionable; omit either and the table stays inert. That is how the public
   * share page and the dynamic-views chart stay read-only without a check of
   * their own — the two surfaces that drill are exactly the two that have an
   * org in hand, so this travels with that decision rather than adding a
   * second switch to keep in sync.
   */
  orgId?: number;
}

/** Default chart area height (px) — dashboard-card scale. */
export const DEFAULT_CHART_HEIGHT = 400;

/** Chart types whose date-granularity x-axes render chronologically. */
const CHRONO_CHART_TYPES: readonly ChartType[] = [
  'bar',
  'line',
  'area',
  'pie',
  'funnel',
  'table',
];

type ChartRendererCoreProps = Omit<
  ChartRendererProps,
  'isLoading' | 'error'
> & {
  isLoading: boolean;
  error: Error | null;
};

/**
 * Inner renderer — accepts already-resolved isLoading/error.
 * Chart data flows from nativeData only.
 *
 * When a converted currency column reports that some amounts used the
 * earliest available exchange rate, the chart gives up one line of its
 * height budget to a caveat beneath it — every surface that renders a chart
 * (tile, focus mode, builder preview, share page) gets the note through this
 * one seam, and a fixed-height tile stays exactly as tall as it was.
 */
export function ChartRendererCore(props: ChartRendererCoreProps) {
  const { isLoading, error, nativeData, height = DEFAULT_CHART_HEIGHT } = props;
  const showEarliestRateNote =
    !isLoading &&
    !error &&
    nativeData !== undefined &&
    hasEarliestRateConversion(nativeData.columns);
  if (!showEarliestRateNote) {
    return <ChartRendererBody {...props} height={height} />;
  }
  return (
    <div className='flex flex-col'>
      <ChartRendererBody
        {...props}
        height={height - EARLIEST_RATE_NOTE_HEIGHT}
      />
      <EarliestRateNote />
    </div>
  );
}

function ChartRendererBody({
  chartType,
  config = DEFAULT_CHART_CONFIG,
  isLoading,
  error,
  resolveLabel,
  dimensionName,
  dimensionNames,
  currency = 'USD',
  nativeData,
  nativeChartType,
  height = DEFAULT_CHART_HEIGHT,
  compact,
  onDatapointSelect,
  activeSelection,
  orgId,
}: ChartRendererCoreProps) {
  // Unique-per-instance id prefix for AreaChartCase's <linearGradient> ids —
  // recharts <defs> ids collide across chart instances/panels sharing a
  // document without one. Must run before any early return (Rules of Hooks).
  const gradientPrefix = useId();

  // Assembled here, above the early returns, for the same Rules-of-Hooks
  // reason as `gradientPrefix` — and memoized because it crosses a prop
  // boundary into a dependency array inside the table.
  //
  // All three inputs must be present: without an org an entity cell has no
  // record to resolve, and without a drill handler a measure cell has nowhere
  // to go. Returning `undefined` when any is missing is what makes an inert
  // table the default rather than something each caller has to ask for.
  const tableInteraction = useMemo<ReportTableInteraction | undefined>(() => {
    if (orgId === undefined || !nativeData || !onDatapointSelect) {
      return undefined;
    }
    return {
      orgId,
      columns: nativeData.columns,
      nativeRows: nativeData.rows,
      onDrill: onDatapointSelect,
      activeSelection,
    };
  }, [orgId, nativeData, onDatapointSelect, activeSelection]);

  if (isLoading) {
    return (
      <div className='flex h-96 items-center justify-center'>
        <LoadingIndicator size='md' label='Loading data…' />
      </div>
    );
  }

  if (error) {
    return (
      <QueryErrorState error={error} label='Error loading chart' size='sm' />
    );
  }

  if (!nativeData) {
    return (
      <div className='rounded-lg bg-[var(--color-bg-secondary)] px-4 py-3 ring-1 ring-inset ring-[var(--color-border-secondary)]'>
        <div className='flex gap-3'>
          <InfoCircle className='h-5 w-5 flex-shrink-0 text-[var(--color-text-primary)]' />
          <p className='text-sm text-[var(--color-text-primary)]'>
            Select dimensions and measures to visualize data
          </p>
        </div>
      </div>
    );
  }

  // A metric is rendered from the native response directly, before the chart
  // transforms run. An ungrouped query has no dimension column, so
  // `nativeToChartData` returns [] and the `chartData.length === 0` empty state
  // below would paint "No data matches this report" over a perfectly good
  // number. Returning here is what keeps a zero-row metric showing 0.
  if (chartType === 'metric') {
    return (
      <MetricChartCase
        nativeData={nativeData}
        currency={currency}
        dimensionName={dimensionName}
        compact={compact}
        onDatapointSelect={onDatapointSelect}
      />
    );
  }

  // Use native data transforms if nativeData is provided
  let chartData: Record<string, unknown>[];
  let tableData: Record<string, unknown>[];
  const columnTitles: Record<string, string> = {};
  const columnTypes: Record<string, string> = {};

  if (nativeData && nativeChartType) {
    // Native reporting path
    chartData = nativeToChartData(nativeData, nativeChartType);
    tableData = nativeToTablePivot(nativeData);

    // Build column metadata from native response
    const tableColumns = nativeToTableColumns(nativeData);
    for (const col of tableColumns) {
      columnTitles[col.key] = col.shortTitle || col.title || col.key;
      if (col.type) {
        columnTypes[col.key] = String(col.type);
      }
    }
  } else {
    // Should never reach here due to early return above
    chartData = [];
    tableData = [];
  }

  // Default axis titles derived from the native response's column metadata,
  // so charts are never unlabeled: the group-by labels name the category
  // axis, and a single measure's label names the value axis (multi-measure
  // charts lean on the legend instead). Saved config labels always win.
  const nativeColumns = nativeData?.columns ?? [];
  const aggregationColumns = nativeColumns.filter(isAggregationColumn);
  const defaultCategoryAxisLabel =
    nativeColumns
      .filter(col => !isAggregationColumn(col))
      .map(col => col.label)
      .join(' › ') || undefined;
  const defaultValueAxisLabel =
    aggregationColumns.length === 1 ? aggregationColumns[0].label : undefined;

  // Derive measure keys and formatting helpers from chart data, excluding the
  // non-measure row keys (see NON_MEASURE_ROW_KEYS).
  const measureKeys =
    chartData.length > 0
      ? Object.keys(chartData[0]).filter(key => !NON_MEASURE_ROW_KEYS.has(key))
      : [];
  // Tooltip formatter: formats values based on measure type (currency vs number)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  const tooltipFormatter = (value: any, name: any, entry: any) => {
    // For pie charts, dataKey is 'value'/'name' (not the Cube key) — use first measureKey
    const rawKey =
      entry?.dataKey && entry.dataKey !== 'value' && entry.dataKey !== 'name'
        ? entry.dataKey
        : (measureKeys[0] ?? String(name ?? ''));
    // A per-currency aggregate (Issue #13623) can't be a single number.
    // When the datapoint carries a breakdown for this measure, render it
    // ("$100,000 | €50,000" / "$100,000" / "—") instead of the raw value.
    const breakdown = currencyBreakdownTooltip(entry?.payload, rawKey);
    if (breakdown !== null) {
      return breakdown;
    }
    let numericValue: number;
    if (typeof value === 'number') {
      numericValue = value;
    } else if (value == null || value === '') {
      numericValue = NaN;
    } else {
      numericValue = Number(value);
    }
    if (!Number.isNaN(numericValue)) {
      return formatMeasureValue(
        numericValue,
        rawKey,
        currency,
        columnTypes[rawKey]
      );
    }
    return value;
  };

  // Identity function for resolveLabel when not provided
  const resolveLabelFn = resolveLabel ?? ((v: string) => v);

  // Transform x values to human-readable labels if resolveLabel is provided
  if (resolveLabel) {
    chartData = chartData.map(item => {
      // Multi-dimension: use xValues array to format each dimension separately
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Chart data structure is dynamic
      const xValues = (item as any).xValues as (string | null)[] | undefined;
      if (
        xValues &&
        xValues.length > 1 &&
        dimensionNames &&
        dimensionNames.length > 1
      ) {
        const formattedParts = xValues
          .map((val, idx) => {
            const dimName = dimensionNames[idx] ?? dimensionName;
            if (val == null || val === 'null' || val === '∅') {
              // Check if this is a date dimension — show date label, otherwise skip nulls
              if (dimName && isDateDimensionWithGranularity(dimName)) {
                return NO_DATE_LABEL;
              }
              return null; // Will be filtered out
            }
            try {
              // Use formatXLabel which handles dates, then resolveLabel
              return formatXLabel(
                val,
                dimName,
                resolveLabel,
                columnTypes?.[dimName]
              );
            } catch (error) {
              logger.error(
                'formatXLabel callback threw error',
                error instanceof Error ? error : undefined,
                {
                  component: 'ChartRenderer',
                  dimensionName: dimName,
                  rawValue: val,
                }
              );
              // Fallback: use formatXLabel without resolveLabel for safe formatting
              return formatXLabel(val, dimName);
            }
          })
          .filter((part): part is string => part != null);

        // Every dimension empty: label the bucket "None", matching
        // formatXLabel's missing-group-value rendering.
        if (formattedParts.length === 0) {
          return { ...item, x: 'None' };
        }
        return {
          ...item,
          x: formattedParts.join(' › '),
        };
      }

      // Single dimension: use formatXLabel which handles dates, then resolveLabel
      try {
        const formatted = formatXLabel(item.x, dimensionName, resolveLabel);
        return {
          ...item,
          x: formatted,
        };
      } catch (error) {
        logger.error(
          'formatXLabel callback threw error',
          error instanceof Error ? error : undefined,
          {
            component: 'ChartRenderer',
            dimensionName,
            rawValue: item.x,
          }
        );
        // Fallback: use formatXLabel without resolveLabel for safe formatting
        return { ...item, x: formatXLabel(item.x, dimensionName) };
      }
    });
  } else {
    // Also handle null for non-resolveLabel case
    chartData = chartData.map(item => ({
      ...item,
      x: formatXLabel(item.x, dimensionName),
    }));
  }

  // Apply Top N filtering if enabled
  chartData = applyTopN(chartData, config.topN);

  // Date-granularity x-axes always render chronologically, across every
  // chart type — the helper is a no-op for non-date dimensions, so non-time
  // group-bys keep the query's order. Runs AFTER applyTopN so Top-N and the
  // backend row limit still select survivors by the query's sort; those
  // survivors then render in date order. Reads the raw ISO x value from
  // xValues[0], which label formatting above preserved (it only rewrote `x`).
  if (CHRONO_CHART_TYPES.includes(chartType)) {
    const primaryDimension = dimensionNames?.[0] ?? dimensionName;
    chartData = sortChartDataChronologically(chartData, primaryDimension);
    // Table and funnel render `tableData` (nativeToTablePivot's stringified
    // rows), not `chartData` — the sort above never reaches them. Their rows
    // carry no xValues, so the raw sort key is read straight off the native
    // response's own primary group-by column key instead.
    if (chartType === 'table' || chartType === 'funnel') {
      const primaryGroupByColumn = nativeColumns.find(
        col => !isAggregationColumn(col)
      );
      tableData = sortTableDataChronologically(
        tableData,
        primaryDimension,
        primaryGroupByColumn?.key
      );
    }
  }

  if (chartData.length === 0) {
    // Zero matching rows is an EMPTY state, not a warning — the query ran
    // fine; there's just nothing in it. Center the shared EmptyState in the
    // pane instead of stretching an alarm-colored banner across it.
    return (
      <div
        className='flex h-full min-h-64 items-center justify-center'
        role='status'
        aria-live='polite'
      >
        <EmptyState
          variant='noData'
          title='No data matches this report'
          description='Try broadening the filters or picking a different date range.'
          size='sm'
        />
      </div>
    );
  }

  // Palette entries are design-token NAMES (see CHART_SERIES_TOKENS) —
  // resolve them to concrete colors for the active theme. Literal colors
  // (saved custom palettes) pass through unchanged.
  const colors = config.colors.palette.map(entry =>
    entry.startsWith('--')
      ? resolveCssColor(entry) || FALLBACK_SERIES_COLOR
      : entry
  );
  // One resolved color per measure key, keyed by name so chart-case
  // components don't need to re-derive the index-into-palette mapping.
  const seriesColors = Object.fromEntries(
    measureKeys.map((key, index) => [key, colors[index % colors.length]])
  );

  // Colour is keyed by MEASURE, which leaves a single-measure chart drawing
  // every mark in palette slot 0 — a dashboard of "X by owner" / "X by stage"
  // bar tiles came out as a wall of one indigo. Where there is one measure
  // and the dimension is nominal, colour by CATEGORY instead, which is what
  // the pie and funnel cases have always done with the same palette.
  //
  // A date-granularity dimension deliberately keeps the single hue: months
  // along an axis are one continuous series, and giving each its own colour
  // implies a distinction between them that doesn't exist.
  const primaryDimensionName = dimensionNames?.[0] ?? dimensionName;
  const categoryColors =
    measureKeys.length === 1 &&
    !(
      primaryDimensionName &&
      isDateDimensionWithGranularity(primaryDimensionName)
    )
      ? chartData.map((_, index) => colors[index % colors.length])
      : undefined;

  // Incommensurate measures (e.g. a currency total alongside a plain
  // count) render as: a shared axis when there's only one unit group
  // ('single'); a bars-and-line combo chart by default when there are
  // exactly two ('combo'); or one stacked panel per unit group when the
  // report opts into that layout, or when there are more than two groups
  // or the bars are horizontal ('panels') — see resolveMultiMeasureLayout
  // and the bar/line/area cases below.
  const unitGroups = groupMeasuresByUnit(measureKeys, columnTypes);
  const barOptions =
    (config.chartOptions as BarChartOptions) || DEFAULT_BAR_OPTIONS;
  const layout = resolveMultiMeasureLayout({
    unitGroups,
    layout: config.multiMeasureLayout,
    barOrientation: chartType === 'bar' ? barOptions.orientation : undefined,
  });

  const axisMeta = computeValueAxisMeta(
    chartData,
    measureKeys,
    columnTypes,
    config,
    currency
  );

  // Map legend align values from our config to Recharts values
  let legendAlign: 'left' | 'center' | 'right';
  if (config.legend.align === 'start') {
    legendAlign = 'left';
  } else if (config.legend.align === 'end') {
    legendAlign = 'right';
  } else {
    legendAlign = 'center';
  }

  // Resolved axis titles for the standard orientation (category along the
  // bottom, value up the left). Horizontal bars swap the defaults so each
  // physical axis still describes what it renders.
  const categoryBottomLabel =
    config.axes?.xAxis.label || defaultCategoryAxisLabel;
  const valueLeftLabel = config.axes?.yAxisLeft.label || defaultValueAxisLabel;

  // Render based on chart type
  switch (chartType) {
    case 'bar': {
      if (layout !== 'single') {
        return (
          <MultiMeasureCartesianChart
            layout={layout}
            chartType={chartType}
            effectiveRenderer='bar'
            unitGroups={unitGroups}
            chartData={chartData}
            config={config}
            columnTitles={columnTitles}
            columnTypes={columnTypes}
            currency={currency}
            seriesColors={seriesColors}
            height={height}
            legendAlign={legendAlign}
            tooltipFormatter={tooltipFormatter}
            categoryBottomLabel={categoryBottomLabel}
            valueLeftLabel={valueLeftLabel}
            gradientPrefix={gradientPrefix}
            lineOptions={DEFAULT_LINE_OPTIONS}
            barStackId={barOptions.stacking !== 'none' ? 'stack' : undefined}
            onDatapointSelect={onDatapointSelect}
            activeSelection={activeSelection}
          />
        );
      }

      return (
        <BarChartCase
          chartData={chartData}
          config={config}
          columnTitles={columnTitles}
          height={height}
          legendAlign={legendAlign}
          tooltipFormatter={tooltipFormatter}
          seriesKeys={measureKeys}
          seriesColors={seriesColors}
          categoryColors={categoryColors}
          axisMeta={axisMeta}
          categoryBottomLabel={categoryBottomLabel}
          valueAxisTitle={valueLeftLabel}
          defaultCategoryAxisLabel={defaultCategoryAxisLabel}
          defaultValueAxisLabel={defaultValueAxisLabel}
          showXAxisLabels
          renderLegend
          onDatapointSelect={onDatapointSelect}
          activeSelection={activeSelection}
        />
      );
    }

    case 'line': {
      const lineOptions =
        (config.chartOptions as LineChartOptions) || DEFAULT_LINE_OPTIONS;

      if (layout !== 'single') {
        return (
          <MultiMeasureCartesianChart
            layout={layout}
            chartType={chartType}
            effectiveRenderer={
              lineOptions.areaFill !== 'none' ? 'area' : 'line'
            }
            unitGroups={unitGroups}
            chartData={chartData}
            config={config}
            columnTitles={columnTitles}
            columnTypes={columnTypes}
            currency={currency}
            seriesColors={seriesColors}
            height={height}
            legendAlign={legendAlign}
            tooltipFormatter={tooltipFormatter}
            categoryBottomLabel={categoryBottomLabel}
            valueLeftLabel={valueLeftLabel}
            gradientPrefix={gradientPrefix}
            lineOptions={lineOptions}
            onDatapointSelect={onDatapointSelect}
            activeSelection={activeSelection}
          />
        );
      }

      if (lineOptions.areaFill !== 'none') {
        return (
          <AreaChartCase
            chartData={chartData}
            config={config}
            columnTitles={columnTitles}
            height={height}
            legendAlign={legendAlign}
            tooltipFormatter={tooltipFormatter}
            seriesKeys={measureKeys}
            seriesColors={seriesColors}
            axisMeta={axisMeta}
            categoryBottomLabel={categoryBottomLabel}
            valueAxisTitle={valueLeftLabel}
            showXAxisLabels
            renderLegend
            lineOptions={lineOptions}
            gradientPrefix={gradientPrefix}
            onDatapointSelect={onDatapointSelect}
            activeSelection={activeSelection}
          />
        );
      }

      return (
        <LineChartCase
          chartData={chartData}
          config={config}
          columnTitles={columnTitles}
          height={height}
          legendAlign={legendAlign}
          tooltipFormatter={tooltipFormatter}
          seriesKeys={measureKeys}
          seriesColors={seriesColors}
          axisMeta={axisMeta}
          categoryBottomLabel={categoryBottomLabel}
          valueAxisTitle={valueLeftLabel}
          showXAxisLabels
          renderLegend
          lineOptions={lineOptions}
          onDatapointSelect={onDatapointSelect}
          activeSelection={activeSelection}
        />
      );
    }

    case 'pie':
      return (
        <PieChartCase
          chartData={chartData}
          config={config}
          colors={colors}
          height={height}
          dimensionName={dimensionName}
          formatXLabel={formatXLabel}
          tooltipFormatter={tooltipFormatter}
          legendAlign={legendAlign}
          formatTotal={value =>
            String(tooltipFormatter(value, 'value', { dataKey: 'value' }))
          }
          onDatapointSelect={onDatapointSelect}
          activeSelection={activeSelection}
        />
      );

    case 'table':
      return (
        // `height` is a budget every case in this switch honours — the table
        // was the one that silently dropped it, so a table tile grew to its
        // row count and (via the dashboard grid's `auto-rows-fr`) dragged
        // every chart beside it to the same height.
        <ReportDataTable
          rows={tableData}
          columnTitles={columnTitles}
          columnTypes={columnTypes}
          resolveLabel={resolveLabelFn}
          currency={currency}
          interaction={tableInteraction}
          maxHeight={height}
        />
      );

    case 'funnel':
      return (
        <FunnelChartCase
          tableData={tableData}
          nativeData={nativeData}
          dimensionName={dimensionName}
          resolveLabel={resolveLabel}
          colors={colors}
          columnTypes={columnTypes}
          currency={currency}
          height={height}
          onDatapointSelect={onDatapointSelect}
          activeSelection={activeSelection}
        />
      );

    case 'area': {
      const lineOptions =
        (config.chartOptions as LineChartOptions) || DEFAULT_AREA_OPTIONS;

      if (layout !== 'single') {
        return (
          <MultiMeasureCartesianChart
            layout={layout}
            chartType={chartType}
            effectiveRenderer='area'
            unitGroups={unitGroups}
            chartData={chartData}
            config={config}
            columnTitles={columnTitles}
            columnTypes={columnTypes}
            currency={currency}
            seriesColors={seriesColors}
            height={height}
            legendAlign={legendAlign}
            tooltipFormatter={tooltipFormatter}
            categoryBottomLabel={categoryBottomLabel}
            valueLeftLabel={valueLeftLabel}
            gradientPrefix={gradientPrefix}
            lineOptions={lineOptions}
            onDatapointSelect={onDatapointSelect}
            activeSelection={activeSelection}
          />
        );
      }

      return (
        <AreaChartCase
          chartData={chartData}
          config={config}
          columnTitles={columnTitles}
          height={height}
          legendAlign={legendAlign}
          tooltipFormatter={tooltipFormatter}
          seriesKeys={measureKeys}
          seriesColors={seriesColors}
          axisMeta={axisMeta}
          categoryBottomLabel={categoryBottomLabel}
          valueAxisTitle={valueLeftLabel}
          showXAxisLabels
          renderLegend
          lineOptions={lineOptions}
          gradientPrefix={gradientPrefix}
          onDatapointSelect={onDatapointSelect}
          activeSelection={activeSelection}
        />
      );
    }

    default:
      return (
        <div className='rounded-lg bg-[var(--color-bg-error-secondary)] px-4 py-3 ring-1 ring-inset ring-[var(--color-border-error)]'>
          <p className='text-sm text-[var(--color-text-error-primary)]'>
            Unsupported chart type: {chartType}
          </p>
        </div>
      );
  }
}
